"use client"
import { NumberInput as UiNumberInput } from "@ark-ui/react/number-input"
import type { ComponentProps } from "react"
import { MdArrowDropDown, MdArrowDropUp } from "react-icons/md"
import { styled } from "styled-system/jsx"

const triggerBase = {
  alignItems: "center",
  cursor: "pointer",
  display: "flex",
  justifyContent: "center",
  textStyle: "input",
  transitionProperty: "[background-color, color]",
  transitionDuration: "[150ms]",
  _icon: {
    flexShrink: 0,
  },

  _hover: {
    backgroundColor: "[#F2F3F7]",
  },
  _active: {
    backgroundColor: "[#E3E4EB]",
  },
} as const

export type RootProps = ComponentProps<typeof Root>
const rootBase = {
  display: "inline-flex",
  alignItems: "center",
  width: "[75px]",
  borderRadius: "[4px]",
  _focusWithin: {
    outline: "[2px solid]",
    outlineColor: "outline",
  },
  _disabled: {
    cursor: "not-allowed",
    opacity: "muted",
  },
} as const

export const Root = styled(UiNumberInput.Root, { base: rootBase })
export const RootProvider = styled(UiNumberInput.RootProvider, {
  base: rootBase,
})
export const DecrementTrigger = styled(
  UiNumberInput.DecrementTrigger,
  {
    base: {
      ...triggerBase,
      borderBottomRightRadius: "[4px]",
    },
  },
  {
    defaultProps: {
      children: <MdArrowDropDown size={14} />,
    },
  },
)
export const IncrementTrigger = styled(
  UiNumberInput.IncrementTrigger,
  {
    base: {
      ...triggerBase,
      borderTopRightRadius: "[4px]",
      borderBottom: true,
    },
  },
  {
    defaultProps: {
      children: <MdArrowDropUp size={14} />,
    },
  },
)

// Mirrors the `Input` base styles; Panda's static extractor cannot follow a
// style object imported from another file, so they are spelled out here.
const Input = styled(UiNumberInput.Input, {
  base: {
    appearance: "none",
    display: "inline-flex",
    alignItems: "center",
    height: "[32px]",
    minHeight: "[32px]",
    minWidth: "[55px]",
    borderRadius: "[4px]",
    borderWidth: "1px",
    border: true,
    backgroundColor: "[white]",
    paddingInline: "[12px]",
    textAlign: "start",
    textStyle: "input",
    transitionProperty:
      "[border-width, background-color, border-color, box-shadow]",
    transitionDuration: "[150ms]",
    _disabled: {
      cursor: "not-allowed",
      opacity: "muted",
    },
    verticalAlign: "top",
    paddingInlineEnd: "[8px]",
    borderTopRightRadius: "[0]",
    borderBottomRightRadius: "[0]",
    borderRightWidth: "0",
    outline: "[0]",
    _focusVisible: {
      outline: "[none]",
    },
  },
})
const Label = styled(UiNumberInput.Label, {
  base: {
    marginInlineStart: "[8px]",
    fontSize: "12px",
    lineHeight: "1.25rem",
    fontWeight: "regular",
    color: "text",
    whiteSpace: "nowrap",
  },
})
const Scrubber = styled(UiNumberInput.Scrubber)
const ValueText = styled(UiNumberInput.ValueText)
const Control = styled(
  UiNumberInput.Control,
  {
    base: {
      height: "[32px]",
      width: "xs",
      display: "flex",
      flexDirection: "column",
      borderWidth: "1px",
      borderLeftWidth: "0",
      border: true,
      borderRadius: "[0 4px 4px 0]",
      backgroundColor: "surface",
      zIndex: "[1]",
    },
  },
  {
    defaultProps: {
      children: (
        <>
          <IncrementTrigger />
          <DecrementTrigger />
        </>
      ),
    },
  },
)

// oxlint-disable-next-line only-export-components
export const NumberInput = {
  Root: Root,
  RootProvider: RootProvider,
  DecrementTrigger: DecrementTrigger,
  IncrementTrigger: IncrementTrigger,
  Input: Input,
  Label: Label,
  Scrubber: Scrubber,
  ValueText: ValueText,
  Control: Control,
} as const
